import { useEffect, useRef } from "react";
import Modal from "../common/Modal";
import { OrderSummary, OrderType } from "../../data/interfaces";
import {
  RiEBike2Line,
  RiShoppingBag3Line,
  RiCloseLine,
  RiTimeLine,
} from "react-icons/ri";

const NewOrderModal: React.FC<{
  order: OrderSummary;
  onAccept: (orderId: string) => void;
  onClose: () => void;
}> = ({ order, onAccept, onClose }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    audioRef.current = new Audio("/assets/sounds/notification.mp3");
    // Loop the sound
    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.play().catch((e) => console.log("Audio play failed", e));
    }

    // Stop and close after 2 minutes
    const timer = setTimeout(
      () => {
        onClose();
      },
      2 * 60 * 1000
    );

    return () => {
      clearTimeout(timer);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [onClose]);

  const handleAccept = () => {
    onAccept(order._id);
  };

  return (
    <Modal
      onClose={onClose}
      contentClassName="p-0 max-w-sm"
    >
      <div className="flex flex-col items-center justify-center w-full bg-white relative overflow-hidden animate-in fade-in zoom-in duration-300 rounded-2xl">
        {/* Header Section with Background Pattern */}
        <div className="w-full bg-gradient-to-br from-primary to-red-600 p-6 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-10 -mt-10 blur-xl"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-black opacity-10 rounded-full -ml-8 -mb-8 blur-lg"></div>

          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20 backdrop-blur-sm"
          >
            <RiCloseLine className="text-lg" />
          </button>

          <div className="flex flex-col items-center relative z-10">
            <div className="w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center mb-3 text-primary animate-bounce">
              <span className="text-2xl font-black">!</span>
            </div>
            <h2 className="text-2xl font-black tracking-tight mb-0.5 text-center">
              Nouvelle Commande
            </h2>
            <p className="text-red-100 text-sm font-medium text-center">
              Une nouvelle commande nécessite votre attention
            </p>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-5 w-full bg-white">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">
                N° Commande
              </span>
              <span className="text-lg font-black text-gray-800">
                #{order.displayId}
              </span>
            </div>

            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-xl border border-gray-100">
              <span className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">
                Montant
              </span>
              <span className="text-lg font-black text-primary">
                {order.paymentInfo.orderPrice.text}
              </span>
            </div>
          </div>

          <div className="space-y-2 mb-6">
            <div className="flex items-center p-2.5 rounded-lg bg-blue-50 text-blue-900 border border-blue-100">
              <div className="w-8 h-8 rounded-md bg-blue-100 flex items-center justify-center mr-3 text-blue-600">
                {order.orderType === OrderType.DELIVERY ? (
                  <RiEBike2Line className="text-lg" />
                ) : (
                  <RiShoppingBag3Line className="text-lg" />
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-blue-400 uppercase leading-none mb-0.5">
                  Type de commande
                </span>
                <span className="font-bold text-sm leading-tight">
                  {order.orderType === OrderType.DELIVERY
                    ? "Livraison à domicile"
                    : "À emporter / Click & Collect"}
                </span>
              </div>
            </div>

            <div className="flex items-center p-2.5 rounded-lg bg-orange-50 text-orange-900 border border-orange-100">
              <div className="w-8 h-8 rounded-md bg-orange-100 flex items-center justify-center mr-3 text-orange-600">
                <RiShoppingBag3Line className="text-lg" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-orange-400 uppercase leading-none mb-0.5">
                  Contenu
                </span>
                <span className="font-bold text-sm leading-tight">
                  {order.items} {order.items > 1 ? "articles" : "article"}
                </span>
              </div>
            </div>

            <div className="flex items-center p-2.5 rounded-lg bg-purple-50 text-purple-900 border border-purple-100">
              <div className="w-8 h-8 rounded-md bg-purple-100 flex items-center justify-center mr-3 text-purple-600">
                <RiTimeLine className="text-lg" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-bold text-purple-400 uppercase leading-none mb-0.5">
                  Reçu à
                </span>
                <span className="font-bold text-sm leading-tight">
                  {order.createdAt}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={handleAccept}
              className="w-full py-3 px-4 rounded-xl bg-green-500 text-white font-bold text-base hover:bg-green-600 transition-all shadow-md shadow-green-200 hover:shadow-green-300 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 relative overflow-hidden group"
            >
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></span>
              <span>Accepter la commande</span>
            </button>

            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 rounded-xl border-2 border-gray-100 text-gray-500 font-bold text-sm hover:bg-gray-50 hover:text-gray-700 hover:border-gray-200 transition-colors"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NewOrderModal;
